import { Flex, FlexProps, Text, useDisclosure } from "@chakra-ui/react";
import TuneUpCheckoutModal from "components/TuneUpCheckoutModal/TuneUpCheckoutModal";
import { IconDoubleArrow, IconDoubleArrowButton } from "icons";
import { useEffect, useState } from "react";
import { Cars } from "api/generated/graphql";
import { CountdownTimer } from "components/CountdownTimer/CountdownTimer";

interface TuneUpButtonProps extends FlexProps {
  carId: string;
  carStats?: Cars["stats"];
  tuneUpAvailable?: boolean;
}

const TuneUpButton = ({ carId, carStats, tuneUpAvailable = false, ...rest }: TuneUpButtonProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [hasActiveTuneUp, setHasActiveTuneUp] = useState(false);
  const [tuneUpEndsIn, setTuneUpEndsIn] = useState<string>("");
  const [latestTuneUpDate, setLatestTuneUpDate] = useState<Date | null>(null);

  useEffect(() => {
    if (!carStats) return;

    // Check if any stat has an active tune-up
    const activeTuneUps = carStats.filter(
      (stat) => stat.tuneUpValidUntil && new Date(stat.tuneUpValidUntil) > new Date()
    );

    if (activeTuneUps.length > 0) {
      // Find the latest tune-up expiration date
      const latestDate = activeTuneUps.reduce((latest, stat) => {
        const current = new Date(stat.tuneUpValidUntil!);
        return latest > current ? latest : current;
      }, new Date(activeTuneUps[0].tuneUpValidUntil!));

      setLatestTuneUpDate(latestDate);
      setHasActiveTuneUp(true);
    } else {
      setHasActiveTuneUp(false);
      setLatestTuneUpDate(null);
    }
  }, [carStats]);

  // Update countdown timer every second
  useEffect(() => {
    if (!latestTuneUpDate) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = latestTuneUpDate.getTime() - now.getTime();
      
      if (difference <= 0) {
        // Tune-up has expired
        setHasActiveTuneUp(false);
        setTuneUpEndsIn("");
        return;
      }

      // Calculate remaining time
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      
      if (days > 0) {
        setTuneUpEndsIn(`${days}d ${hours}h`);
      } else if (hours > 0) {
        setTuneUpEndsIn(`${hours}h ${minutes}m`);
      } else {
        setTuneUpEndsIn(`${minutes}m`);
      }
    };

    calculateTimeLeft();
    const timerId = setInterval(calculateTimeLeft, 60000); // Update every minute

    return () => clearInterval(timerId);
  }, [latestTuneUpDate]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (tuneUpAvailable) {
      onOpen();
    }
  };

  // If there's no active tune-up and tune-ups aren't available, don't render the component
  if (!hasActiveTuneUp && !tuneUpAvailable) {
    return null;
  }

  return (
    <>
      <Flex
        background={"linear-gradient(225deg, rgba(252, 244, 191, 0.2) 0%, rgba(239, 200, 98, 0.2) 18%, rgba(205, 152, 50, 0.2) 73%, rgba(187, 129, 27, 0.2) 100%)"}
        _hover={{
          background: "linear-gradient(225deg, rgba(252, 244, 191, 0.4) 0%, rgba(239, 200, 98, 0.4) 18%, rgba(205, 152, 50, 0.4) 73%, rgba(187, 129, 27, 0.4) 100%)",
        }}
        transition="all 0.3s ease-in-out"
        position="relative"
        alignItems="center"
        justifyContent="center"
        width="full"
        minWidth={{ base: '141px', md: '166px' }}
        height={{ base: '22px', md: '32px' }}
        gap="2"
        px="4"
        borderRadius="4px"
        cursor={tuneUpAvailable ? "pointer" : "default"}
        _before={{
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "4px",
          padding: "2px",
          background: "linear-gradient(360deg, rgba(229, 186, 85, 0.6) 0%, rgba(229, 186, 85, 0.24) 100%)",
          mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          maskComposite: "exclude",
          zIndex: -1,
        }}
        {...rest}
        onClick={handleClick}
      >
        <IconDoubleArrowButton />

        <Text
          fontSize={{ base: '0.66rem', md: '0.79rem' }}
          lineHeight={{ base: '0.875rem', md: '1rem' }}
          color={hasActiveTuneUp ? "rgba(255, 221, 86, 1)" : "rgba(255, 221, 86, 0.8)"}
          _hover={{
            color: "rgba(255, 221, 86, 1)"
          }}
          fontFamily="heading"
          fontWeight={400}
          transition="color 0.2s ease-in-out"
        >
          {hasActiveTuneUp ? "TUNED-UP!" : "TUNE-UP CAR"} 
        </Text>

        {hasActiveTuneUp && tuneUpEndsIn && (
          <Flex
            alignItems="center"
            justifyContent="center"
            gap="0.25rem"
          >
            <Text
              fontSize={{ base: '0.66rem', md: '0.79rem' }}
              lineHeight={{ base: '0.875rem', md: '1rem' }}
              fontWeight="500"
              color="treasuredTeal.100"
            >
              ENDS
            </Text>

            <CountdownTimer timeLeft={latestTuneUpDate?.toISOString() || ""} showMinutes={false} />
          </Flex>
        )}

        <IconDoubleArrowButton 
          transform="rotate(180deg)"
        />
      </Flex>

      <TuneUpCheckoutModal isOpen={isOpen} onClose={onClose} carId={carId} />
    </>
  );
};

export default TuneUpButton;
